import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaEye, FaPlus, FaSearch, FaToggleOff, FaToggleOn } from 'react-icons/fa';
import DataTable from '../DataTable/DataTable.jsx';
import Pagination from '../Pagination/Pagination.jsx';
import assinaturasApi from '../../services/assinaturasApi.js';
import planosApi from '../../services/planosApi.js';
import financeiroApi from '../../services/financeiroApi.js';
import { colors } from '../../utils/colors.js';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function formatCentavos(centavos) {
  if (centavos === null || centavos === undefined) return '—';
  return currencyFormatter.format(centavos / 100);
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('pt-BR') : '—';
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—';
}

const STATUS_LABELS = {
  ativa: 'Ativa',
  pausada: 'Pausada',
  cancelada: 'Cancelada',
  falhada: 'Falhada',
  pendente: 'Pendente',
};

const STATUS_TONE = {
  ativa: 'success',
  pausada: 'warning',
  cancelada: 'gray',
  falhada: 'error',
  pendente: 'info',
};

const EXTRATO_TIPO_LABELS = {
  entrada: 'Entrada',
  saida: 'Saída',
};

const EXTRATO_TIPO_TONE = {
  entrada: 'success',
  saida: 'error',
};

const TRANSACAO_STATUS_LABELS = {
  pendente: 'Pendente',
  pago: 'Pago',
  falhado: 'Falhado',
};

const TRANSACAO_STATUS_TONE = {
  pendente: 'info',
  pago: 'success',
  falhado: 'error',
};

const EMPTY_PLANO_FORM = {
  nome: '',
  slug: '',
  tipo: 'default',
  valor_reais: '0.00',
  creditos_mensais: '0',
  status: 'active',
  descricao: '',
};

function planoToForm(p) {
  return {
    nome: p.nome,
    slug: p.slug,
    tipo: p.tipo || 'default',
    valor_reais: (p.valor_centavos / 100).toFixed(2),
    creditos_mensais: String(p.creditos_mensais ?? 0),
    status: p.status,
    descricao: p.descricao || '',
  };
}

function formToPlanoPayload(form) {
  const valorNormalizado = String(form.valor_reais).replace(',', '.');
  return {
    nome: form.nome,
    slug: form.slug,
    tipo: form.tipo,
    valor_centavos: Math.round((Number(valorNormalizado) || 0) * 100),
    creditos_mensais: Number(form.creditos_mensais) || 0,
    status: form.status,
    descricao: form.descricao || null,
  };
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const TabsBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${colors.gray[200]};
  margin-bottom: 20px;
`;

const TabButton = styled.button`
  padding: 10px 4px;
  margin-right: 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$active ? colors.primary[600] : colors.gray[500])};
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 240px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const FilterSelect = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const DateFieldGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateFieldLabel = styled.span`
  font-size: 12px;
  color: ${colors.gray[500]};
`;

const DateInput = styled.input`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const UsuarioEmail = styled.div`
  font-size: 12px;
  color: ${colors.gray[500]};
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(props) => {
    if (props.$tone === 'success') return colors.success.light;
    if (props.$tone === 'warning') return colors.warning.light;
    if (props.$tone === 'error') return colors.error.light;
    if (props.$tone === 'info') return colors.info.light;
    return colors.gray[100];
  }};
  color: ${(props) => {
    if (props.$tone === 'success') return colors.success.dark;
    if (props.$tone === 'warning') return colors.warning.dark;
    if (props.$tone === 'error') return colors.error.dark;
    if (props.$tone === 'info') return colors.info.dark;
    return colors.gray[600];
  }};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.error.main : colors.gray[500])};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${(props) => (props.$danger ? colors.error.dark : colors.primary[600])};
  }
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 20px;
  background: ${colors.gray[50]};
  color: ${colors.gray[600]};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 20px;
  border-top: 1px solid ${colors.gray[100]};
  color: ${colors.gray[800]};
  white-space: nowrap;
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const ModalCard = styled.form`
  background: ${colors.white};
  overflow-y: auto;
  width: 480px;
  max-width: 100%;
  height: 100vh;
  padding: 28px;

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  text-transform: uppercase;
  color: ${colors.gray[500]};
  margin: 20px 0 10px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${colors.gray[800]};
  padding: 6px 0;
  border-bottom: 1px solid ${colors.gray[100]};
`;

const InfoLabel = styled.span`
  color: ${colors.gray[500]};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$cols || '1fr'};
  gap: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 70px;
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const DangerButton = styled(Button)`
  background: ${colors.error.light};
  color: ${colors.error.dark};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const HintText = styled.p`
  font-size: 12px;
  color: ${colors.gray[500]};
  margin: 4px 0 16px;
`;

const DEBOUNCE_MS = 400;

export default function AssinaturaManager() {
  const [tab, setTab] = useState('assinaturas');
  const [planos, setPlanos] = useState([]);

  const loadPlanos = useCallback(() => {
    planosApi
      .list()
      .then((data) => setPlanos(data.items || []))
      .catch(() => setPlanos([]));
  }, []);

  useEffect(() => {
    loadPlanos();
  }, [loadPlanos]);

  // --- Assinaturas tab ---
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planoFilter, setPlanoFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter) params.status_assinatura = statusFilter;
    if (planoFilter) params.plano_id = planoFilter;
    assinaturasApi
      .list(params)
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, debouncedSearch, statusFilter, planoFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Detail drawer ---
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const openDetail = (id) => {
    setShowDetail(true);
    setDetail(null);
    setDetailError('');
    setCancelMotivo('');
    setDetailLoading(true);
    assinaturasApi
      .get(id)
      .then(setDetail)
      .catch(() => setDetailError('Não foi possível carregar os detalhes desta assinatura.'))
      .finally(() => setDetailLoading(false));
  };

  const handleCancel = async () => {
    if (!detail) return;
    if (!window.confirm('Cancelar esta assinatura? A ação também replica o cancelamento no gateway de pagamento.')) {
      return;
    }
    setCancelLoading(true);
    try {
      await assinaturasApi.cancel(detail.id, cancelMotivo || undefined);
      const updated = await assinaturasApi.get(detail.id);
      setDetail(updated);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível cancelar a assinatura.');
    } finally {
      setCancelLoading(false);
    }
  };

  const columns = [
    {
      key: 'usuario',
      label: 'Usuário',
      render: (row) => (
        <div>
          <div>{row.usuario_nome || '—'}</div>
          <UsuarioEmail>{row.usuario_email || row.usuario_id}</UsuarioEmail>
        </div>
      ),
    },
    { key: 'plano_nome', label: 'Plano', render: (row) => row.plano_nome || '—' },
    { key: 'valor', label: 'Valor', render: (row) => formatCentavos(row.plano_valor_centavos) },
    {
      key: 'status_assinatura',
      label: 'Status',
      render: (row) => (
        <Badge $tone={STATUS_TONE[row.status_assinatura] || 'gray'}>
          {STATUS_LABELS[row.status_assinatura] || row.status_assinatura}
        </Badge>
      ),
    },
    { key: 'data_renovacao', label: 'Vencimento', render: (row) => formatDate(row.data_renovacao) },
    {
      key: 'acoes',
      label: 'Ações',
      render: (row) => (
        <IconButton type="button" onClick={() => openDetail(row.id)} title="Ver detalhes">
          <FaEye />
        </IconButton>
      ),
    },
  ];

  // --- Planos tab ---
  const [showPlanoModal, setShowPlanoModal] = useState(false);
  const [editingPlano, setEditingPlano] = useState(null);
  const [planoForm, setPlanoForm] = useState(EMPTY_PLANO_FORM);
  const [planoFormError, setPlanoFormError] = useState('');
  const [planosLoading, setPlanosLoading] = useState(false);

  const openCreatePlano = () => {
    setEditingPlano(null);
    setPlanoForm(EMPTY_PLANO_FORM);
    setPlanoFormError('');
    setShowPlanoModal(true);
  };

  const openEditPlano = (plano) => {
    setEditingPlano(plano);
    setPlanoForm(planoToForm(plano));
    setPlanoFormError('');
    setShowPlanoModal(true);
  };

  const handleTogglePlanoStatus = async (plano) => {
    const novoStatus = plano.status === 'active' ? 'inactive' : 'active';
    try {
      await planosApi.update(plano.id, { status: novoStatus });
      loadPlanos();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar o status do plano.');
    }
  };

  const handleSubmitPlano = async (e) => {
    e.preventDefault();
    setPlanoFormError('');
    setPlanosLoading(true);
    const payload = formToPlanoPayload(planoForm);
    try {
      if (editingPlano) {
        await planosApi.update(editingPlano.id, payload);
      } else {
        await planosApi.create(payload);
      }
      setShowPlanoModal(false);
      loadPlanos();
    } catch (err) {
      setPlanoFormError(err.response?.data?.detail || 'Não foi possível salvar o plano.');
    } finally {
      setPlanosLoading(false);
    }
  };

  // --- Extrato tab ---
  const [extratoItems, setExtratoItems] = useState(null);
  const [extratoTotal, setExtratoTotal] = useState(0);
  const [extratoLoading, setExtratoLoading] = useState(true);
  const [extratoSearch, setExtratoSearch] = useState('');
  const [extratoDebouncedSearch, setExtratoDebouncedSearch] = useState('');
  const [extratoTipo, setExtratoTipo] = useState('');
  const [extratoDataInicio, setExtratoDataInicio] = useState('');
  const [extratoDataFim, setExtratoDataFim] = useState('');
  const [extratoPage, setExtratoPage] = useState(1);
  const [extratoPerPage, setExtratoPerPage] = useState(20);
  const extratoDebounceRef = useRef(null);

  useEffect(() => {
    if (extratoDebounceRef.current) clearTimeout(extratoDebounceRef.current);
    extratoDebounceRef.current = setTimeout(() => {
      setExtratoDebouncedSearch(extratoSearch);
      setExtratoPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(extratoDebounceRef.current);
  }, [extratoSearch]);

  const loadExtrato = useCallback(() => {
    setExtratoLoading(true);
    const params = { page: extratoPage, per_page: extratoPerPage };
    if (extratoDebouncedSearch) params.search = extratoDebouncedSearch;
    if (extratoTipo) params.tipo = extratoTipo;
    if (extratoDataInicio) params.data_inicio = extratoDataInicio;
    if (extratoDataFim) params.data_fim = extratoDataFim;
    financeiroApi
      .listExtrato(params)
      .then((data) => {
        setExtratoItems(data.items || []);
        setExtratoTotal(data.total || 0);
      })
      .catch(() => {
        setExtratoItems([]);
        setExtratoTotal(0);
      })
      .finally(() => setExtratoLoading(false));
  }, [extratoPage, extratoPerPage, extratoDebouncedSearch, extratoTipo, extratoDataInicio, extratoDataFim]);

  useEffect(() => {
    if (tab === 'extrato') loadExtrato();
  }, [tab, loadExtrato]);

  const extratoColumns = [
    {
      key: 'usuario',
      label: 'Usuário',
      render: (row) => (
        <div>
          <div>{row.usuario_nome || '—'}</div>
          <UsuarioEmail>{row.usuario_email || row.usuario_id}</UsuarioEmail>
        </div>
      ),
    },
    {
      key: 'tipo',
      label: 'Tipo',
      render: (row) => (
        <Badge $tone={EXTRATO_TIPO_TONE[row.tipo] || 'gray'}>{EXTRATO_TIPO_LABELS[row.tipo] || row.tipo}</Badge>
      ),
    },
    { key: 'valor', label: 'Valor', render: (row) => row.valor?.toFixed?.(2) ?? row.valor },
    { key: 'saldo_apos', label: 'Saldo após', render: (row) => row.saldo_apos?.toFixed?.(2) ?? row.saldo_apos },
    { key: 'descricao', label: 'Descrição', render: (row) => row.descricao || '—' },
    { key: 'created_at', label: 'Data', render: (row) => formatDateTime(row.created_at) },
  ];

  // --- Transações tab ---
  const [transacaoItems, setTransacaoItems] = useState(null);
  const [transacaoTotal, setTransacaoTotal] = useState(0);
  const [transacaoLoading, setTransacaoLoading] = useState(true);
  const [transacaoSearch, setTransacaoSearch] = useState('');
  const [transacaoDebouncedSearch, setTransacaoDebouncedSearch] = useState('');
  const [transacaoStatus, setTransacaoStatus] = useState('');
  const [transacaoDataInicio, setTransacaoDataInicio] = useState('');
  const [transacaoDataFim, setTransacaoDataFim] = useState('');
  const [transacaoPage, setTransacaoPage] = useState(1);
  const [transacaoPerPage, setTransacaoPerPage] = useState(20);
  const transacaoDebounceRef = useRef(null);

  useEffect(() => {
    if (transacaoDebounceRef.current) clearTimeout(transacaoDebounceRef.current);
    transacaoDebounceRef.current = setTimeout(() => {
      setTransacaoDebouncedSearch(transacaoSearch);
      setTransacaoPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(transacaoDebounceRef.current);
  }, [transacaoSearch]);

  const loadTransacoes = useCallback(() => {
    setTransacaoLoading(true);
    const params = { page: transacaoPage, per_page: transacaoPerPage };
    if (transacaoDebouncedSearch) params.search = transacaoDebouncedSearch;
    if (transacaoStatus) params.status = transacaoStatus;
    if (transacaoDataInicio) params.data_inicio = transacaoDataInicio;
    if (transacaoDataFim) params.data_fim = transacaoDataFim;
    financeiroApi
      .listTransacoes(params)
      .then((data) => {
        setTransacaoItems(data.items || []);
        setTransacaoTotal(data.total || 0);
      })
      .catch(() => {
        setTransacaoItems([]);
        setTransacaoTotal(0);
      })
      .finally(() => setTransacaoLoading(false));
  }, [transacaoPage, transacaoPerPage, transacaoDebouncedSearch, transacaoStatus, transacaoDataInicio, transacaoDataFim]);

  useEffect(() => {
    if (tab === 'transacoes') loadTransacoes();
  }, [tab, loadTransacoes]);

  const transacaoColumns = [
    {
      key: 'usuario',
      label: 'Usuário',
      render: (row) => (
        <div>
          <div>{row.usuario_nome || '—'}</div>
          <UsuarioEmail>{row.usuario_email || row.usuario_id}</UsuarioEmail>
        </div>
      ),
    },
    { key: 'valor_total_centavos', label: 'Valor', render: (row) => formatCentavos(row.valor_total_centavos) },
    { key: 'quantidade_creditos', label: 'Créditos', render: (row) => row.quantidade_creditos },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <Badge $tone={TRANSACAO_STATUS_TONE[row.status] || 'gray'}>
          {TRANSACAO_STATUS_LABELS[row.status] || row.status}
        </Badge>
      ),
    },
    { key: 'provedor_nome', label: 'Provedor', render: (row) => row.provedor_nome },
    { key: 'referencia_externa_id', label: 'Referência externa', render: (row) => row.referencia_externa_id || '—' },
    { key: 'created_at', label: 'Data', render: (row) => formatDateTime(row.created_at) },
  ];

  return (
    <div>
      <Header>
        <Title>Assinaturas</Title>
      </Header>

      <TabsBar>
        <TabButton type="button" $active={tab === 'assinaturas'} onClick={() => setTab('assinaturas')}>
          Assinaturas
        </TabButton>
        <TabButton type="button" $active={tab === 'planos'} onClick={() => setTab('planos')}>
          Planos
        </TabButton>
        <TabButton type="button" $active={tab === 'extrato'} onClick={() => setTab('extrato')}>
          Extrato
        </TabButton>
        <TabButton type="button" $active={tab === 'transacoes'} onClick={() => setTab('transacoes')}>
          Transações
        </TabButton>
      </TabsBar>

      {tab === 'assinaturas' && (
        <>
          <FilterBar>
            <SearchBox>
              <SearchIcon />
              <SearchInput
                placeholder="Buscar por nome, apelido ou email do usuário..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os status</option>
              {Object.keys(STATUS_LABELS).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={planoFilter}
              onChange={(e) => {
                setPlanoFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os planos</option>
              {planos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </FilterSelect>
          </FilterBar>

          <DataTable
            columns={columns}
            rows={items}
            loading={loading}
            emptyMessage="Nenhuma assinatura encontrada."
          />
          {!loading && (
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              onPageChange={setPage}
              onPerPageChange={(value) => {
                setPerPage(value);
                setPage(1);
              }}
            />
          )}
        </>
      )}

      {tab === 'planos' && (
        <>
          <Header>
            <div />
            <AddButton type="button" onClick={openCreatePlano}>
              <FaPlus /> Novo plano
            </AddButton>
          </Header>

          <List>
            {!planos.length && <Empty>Nenhum plano cadastrado ainda.</Empty>}
            {planos.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <Th>Nome</Th>
                    <Th>Slug</Th>
                    <Th>Tipo</Th>
                    <Th>Valor</Th>
                    <Th>Créditos/mês</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {planos.map((p) => (
                    <tr key={p.id}>
                      <Td>{p.nome}</Td>
                      <Td>{p.slug}</Td>
                      <Td>{p.tipo}</Td>
                      <Td>{formatCentavos(p.valor_centavos)}</Td>
                      <Td>{p.creditos_mensais}</Td>
                      <Td>
                        <Badge $tone={p.status === 'active' ? 'success' : 'gray'}>{p.status}</Badge>
                      </Td>
                      <Td>
                        <IconButton
                          type="button"
                          onClick={() => handleTogglePlanoStatus(p)}
                          title={p.status === 'active' ? 'Inativar' : 'Ativar'}
                        >
                          {p.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                        </IconButton>
                        <IconButton type="button" onClick={() => openEditPlano(p)} title="Editar">
                          Editar
                        </IconButton>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </List>
        </>
      )}

      {tab === 'extrato' && (
        <>
          <FilterBar>
            <SearchBox>
              <SearchIcon />
              <SearchInput
                placeholder="Buscar por nome, apelido ou email do usuário..."
                value={extratoSearch}
                onChange={(e) => setExtratoSearch(e.target.value)}
              />
            </SearchBox>
            <FilterSelect
              value={extratoTipo}
              onChange={(e) => {
                setExtratoTipo(e.target.value);
                setExtratoPage(1);
              }}
            >
              <option value="">Todos os tipos</option>
              {Object.keys(EXTRATO_TIPO_LABELS).map((t) => (
                <option key={t} value={t}>
                  {EXTRATO_TIPO_LABELS[t]}
                </option>
              ))}
            </FilterSelect>
            <DateFieldGroup>
              <DateFieldLabel>De</DateFieldLabel>
              <DateInput
                type="date"
                value={extratoDataInicio}
                onChange={(e) => {
                  setExtratoDataInicio(e.target.value);
                  setExtratoPage(1);
                }}
              />
            </DateFieldGroup>
            <DateFieldGroup>
              <DateFieldLabel>Até</DateFieldLabel>
              <DateInput
                type="date"
                value={extratoDataFim}
                onChange={(e) => {
                  setExtratoDataFim(e.target.value);
                  setExtratoPage(1);
                }}
              />
            </DateFieldGroup>
          </FilterBar>

          <DataTable
            columns={extratoColumns}
            rows={extratoItems}
            loading={extratoLoading}
            emptyMessage="Nenhum lançamento encontrado."
          />
          {!extratoLoading && (
            <Pagination
              page={extratoPage}
              perPage={extratoPerPage}
              total={extratoTotal}
              onPageChange={setExtratoPage}
              onPerPageChange={(value) => {
                setExtratoPerPage(value);
                setExtratoPage(1);
              }}
            />
          )}
        </>
      )}

      {tab === 'transacoes' && (
        <>
          <FilterBar>
            <SearchBox>
              <SearchIcon />
              <SearchInput
                placeholder="Buscar por nome, apelido ou email do usuário..."
                value={transacaoSearch}
                onChange={(e) => setTransacaoSearch(e.target.value)}
              />
            </SearchBox>
            <FilterSelect
              value={transacaoStatus}
              onChange={(e) => {
                setTransacaoStatus(e.target.value);
                setTransacaoPage(1);
              }}
            >
              <option value="">Todos os status</option>
              {Object.keys(TRANSACAO_STATUS_LABELS).map((s) => (
                <option key={s} value={s}>
                  {TRANSACAO_STATUS_LABELS[s]}
                </option>
              ))}
            </FilterSelect>
            <DateFieldGroup>
              <DateFieldLabel>De</DateFieldLabel>
              <DateInput
                type="date"
                value={transacaoDataInicio}
                onChange={(e) => {
                  setTransacaoDataInicio(e.target.value);
                  setTransacaoPage(1);
                }}
              />
            </DateFieldGroup>
            <DateFieldGroup>
              <DateFieldLabel>Até</DateFieldLabel>
              <DateInput
                type="date"
                value={transacaoDataFim}
                onChange={(e) => {
                  setTransacaoDataFim(e.target.value);
                  setTransacaoPage(1);
                }}
              />
            </DateFieldGroup>
          </FilterBar>

          <DataTable
            columns={transacaoColumns}
            rows={transacaoItems}
            loading={transacaoLoading}
            emptyMessage="Nenhuma transação encontrada."
          />
          {!transacaoLoading && (
            <Pagination
              page={transacaoPage}
              perPage={transacaoPerPage}
              total={transacaoTotal}
              onPageChange={setTransacaoPage}
              onPerPageChange={(value) => {
                setTransacaoPerPage(value);
                setTransacaoPage(1);
              }}
            />
          )}
        </>
      )}

      {showDetail &&
        createPortal(
          <ModalOverlay onClick={() => setShowDetail(false)}>
            <ModalCard as="div" onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Detalhes da assinatura</ModalTitle>

              {detailLoading && <Empty>Carregando...</Empty>}
              {detailError && <ErrorBox>{detailError}</ErrorBox>}

              {detail && (
                <>
                  <SectionTitle>Usuário</SectionTitle>
                  <InfoRow>
                    <InfoLabel>Nome</InfoLabel>
                    <span>{detail.usuario?.nome}</span>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Email</InfoLabel>
                    <span>{detail.usuario?.email}</span>
                  </InfoRow>

                  <SectionTitle>Assinatura</SectionTitle>
                  <InfoRow>
                    <InfoLabel>Plano</InfoLabel>
                    <span>{detail.plano?.nome || '—'}</span>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Valor</InfoLabel>
                    <span>{formatCentavos(detail.plano?.valor_centavos)}</span>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Status</InfoLabel>
                    <Badge $tone={STATUS_TONE[detail.status_assinatura] || 'gray'}>
                      {STATUS_LABELS[detail.status_assinatura] || detail.status_assinatura}
                    </Badge>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Vencimento</InfoLabel>
                    <span>{formatDate(detail.data_renovacao)}</span>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Criada em</InfoLabel>
                    <span>{formatDateTime(detail.created_at)}</span>
                  </InfoRow>
                  {detail.cancelado_em && (
                    <InfoRow>
                      <InfoLabel>Cancelada em</InfoLabel>
                      <span>{formatDateTime(detail.cancelado_em)}</span>
                    </InfoRow>
                  )}

                  <SectionTitle>Pagamentos do usuário</SectionTitle>
                  <HintText>
                    Últimas transações deste usuário (histórico não é vinculado exclusivamente a esta assinatura).
                  </HintText>
                  {(!detail.pagamentos_usuario || detail.pagamentos_usuario.length === 0) && (
                    <Empty>Nenhum pagamento encontrado.</Empty>
                  )}
                  {detail.pagamentos_usuario?.map((t) => (
                    <InfoRow key={t.id}>
                      <InfoLabel>
                        {formatDate(t.created_at)} — {t.provedor_nome}
                      </InfoLabel>
                      <span>
                        {formatCentavos(t.valor_total_centavos)} ({t.status})
                      </span>
                    </InfoRow>
                  ))}

                  {detail.status_assinatura !== 'cancelada' && (
                    <>
                      <SectionTitle>Cancelar assinatura</SectionTitle>
                      <Input
                        placeholder="Motivo do cancelamento (opcional)"
                        value={cancelMotivo}
                        onChange={(e) => setCancelMotivo(e.target.value)}
                      />
                      <ModalActions>
                        <DangerButton type="button" onClick={handleCancel} disabled={cancelLoading}>
                          {cancelLoading ? 'Cancelando...' : 'Cancelar assinatura'}
                        </DangerButton>
                      </ModalActions>
                    </>
                  )}
                </>
              )}

              <ModalActions>
                <CancelButton type="button" onClick={() => setShowDetail(false)}>
                  Fechar
                </CancelButton>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}

      {showPlanoModal &&
        createPortal(
          <ModalOverlay onClick={() => setShowPlanoModal(false)}>
            <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmitPlano}>
              <ModalTitle>{editingPlano ? 'Editar plano' : 'Novo plano'}</ModalTitle>

              {planoFormError && <ErrorBox>{planoFormError}</ErrorBox>}

              <Label>Nome</Label>
              <Input
                required
                value={planoForm.nome}
                onChange={(e) => setPlanoForm({ ...planoForm, nome: e.target.value })}
              />

              <Label>Slug</Label>
              <Input
                required
                value={planoForm.slug}
                onChange={(e) => setPlanoForm({ ...planoForm, slug: e.target.value })}
              />

              <Row $cols="1fr 1fr">
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={planoForm.tipo}
                    onChange={(e) => setPlanoForm({ ...planoForm, tipo: e.target.value })}
                  >
                    <option value="default">Padrão</option>
                    <option value="ferramenta">Ferramenta</option>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={planoForm.status}
                    onChange={(e) => setPlanoForm({ ...planoForm, status: e.target.value })}
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </Select>
                </div>
              </Row>

              <Row $cols="1fr 1fr">
                <div>
                  <Label>Valor (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planoForm.valor_reais}
                    onChange={(e) => setPlanoForm({ ...planoForm, valor_reais: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Créditos mensais</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planoForm.creditos_mensais}
                    onChange={(e) => setPlanoForm({ ...planoForm, creditos_mensais: e.target.value })}
                  />
                </div>
              </Row>

              <Label>Descrição</Label>
              <TextArea
                value={planoForm.descricao}
                onChange={(e) => setPlanoForm({ ...planoForm, descricao: e.target.value })}
              />

              <ModalActions>
                <CancelButton type="button" onClick={() => setShowPlanoModal(false)}>
                  Cancelar
                </CancelButton>
                <SaveButton type="submit" disabled={planosLoading}>
                  {planosLoading ? 'Salvando...' : 'Salvar'}
                </SaveButton>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}
    </div>
  );
}
